// elekt.io's page script. Small on purpose: the pages are server-rendered and work
// without it; this only adds what a page cannot know before it loads.
//
//   * who is signed in (GET /me.json, private) - the account menu
//   * source popovers that open on hover for a mouse, and stay inside the viewport
//   * countdowns re-worded from the visitor's own calendar day
//   * the filter form that submits on change, the country search, and the World Bank
//     figures a country page fetches after it loads
//
// No framework, no Turbo, no inline script (the HTML is cached and shared, so it
// carries no per-request nonce). Leaflet and Chart.js live in their own entries
// (elekt_map.js, elekt_chart.js) and load only on the pages that draw them.
import "./elekt/site.css"

const ready = (fn) => (document.readyState === "loading" ? document.addEventListener("DOMContentLoaded", fn) : fn())

// ---- account ----------------------------------------------------------------

async function loadMe() {
    const host = document.querySelector("[data-ek-account]")
    if (!host) return
    try {
        const response = await fetch(host.dataset.meUrl, { credentials: "same-origin", headers: { Accept: "application/json" } })
        if (!response.ok) return
        const me = await response.json()
        window.elektMe = me
        document.dispatchEvent(new CustomEvent("elekt:me", { detail: me }))
        if (me.signed_in) renderAccount(host, me)
    } catch (_error) {
        // Offline or blocked: the "Sign in" link the server rendered stays.
    }
}

function renderAccount(host, me) {
    const name = document.createElement("span")
    name.className = "ek-account-name"
    name.textContent = me.name || "You"

    const form = document.createElement("form")
    form.method = "post"
    form.action = me.sign_out_path
    form.className = "ek-account-form"
    for (const [key, value] of [["_method", "delete"], ["authenticity_token", me.csrf_token]]) {
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = key
        input.value = value
        form.append(input)
    }
    const button = document.createElement("button")
    button.type = "submit"
    button.className = "ek-btn ek-btn--ghost ek-btn--small"
    button.textContent = "Sign out"
    form.append(button)

    const alerts = document.createElement("a")
    alerts.href = me.alerts_path
    alerts.className = "ek-nav-link"
    alerts.textContent = "Alerts"

    host.replaceChildren(name, alerts, form)
}

// ---- follow ------------------------------------------------------------------

// Every page ships the same "Follow <country>" link to sign in. Once /me.json says
// someone is signed in, it becomes a toggle. Countries only: there is nothing to
// follow here but a country.
function initFollow(me) {
    if (!me.signed_in) return
    for (const host of document.querySelectorAll("[data-ek-follow]")) {
        const iso3 = host.dataset.ekFollow
        const name = host.dataset.name
        const note = host.querySelector("[data-ek-follow-note]")
        const button = document.createElement("button")
        button.type = "button"
        button.className = "ek-btn ek-btn--small"
        button.title = "Get an alert when a date moves or results come in"
        const status = document.createElement("span")
        status.className = "ek-visually-hidden"
        status.setAttribute("role", "status")

        let following = (me.follows || []).includes(iso3)
        const paint = () => {
            button.setAttribute("aria-pressed", String(following))
            button.textContent = following ? `Following ${name}` : `Follow ${name}`
            button.classList.toggle("ek-btn--ghost", following)
            if (note) {
                note.textContent = following ? "Alerts on. " : note.dataset.original
                if (following) {
                    const link = document.createElement("a")
                    link.href = host.dataset.alertsPath
                    link.textContent = "Manage alerts"
                    note.append(link)
                }
            }
        }
        if (note) note.dataset.original = note.textContent
        button.addEventListener("click", async () => {
            button.disabled = true
            try {
                const response = await fetch(following ? `${host.dataset.followsPath}/${iso3}` : host.dataset.followsPath, {
                    method: following ? "DELETE" : "POST",
                    credentials: "same-origin",
                    headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": me.csrf_token },
                    body: following ? undefined : JSON.stringify({ iso3 })
                })
                if (!response.ok) throw new Error(String(response.status))
                following = !following
                status.textContent = following ? `Now following ${name}` : `Stopped following ${name}`
                paint()
            } catch (_error) {
                status.textContent = "That did not work. Try again."
            } finally {
                button.disabled = false
            }
        })
        paint()
        host.querySelector("[data-ek-follow-fallback]")?.replaceWith(button, status)
    }
}

// ---- suggested for you ---------------------------------------------------------

// The countries a signed-in visitor's Legal.id points at (citizenship, home) and their US state, offered on the
// home page with a Follow button each. /me.json carries them; "Not now" forgets them for the session.
function initSuggestions(me) {
    const host = document.querySelector("[data-ek-suggest]")
    const suggestions = me.signed_in && me.suggestions
    if (!host || !suggestions) return
    const list = host.querySelector("[data-ek-suggest-list]")
    const headers = { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": me.csrf_token }
    const items = []
    for (const country of suggestions.countries || []) {
        const item = document.createElement("li")
        item.className = "ek-suggest-item"
        const link = document.createElement("a")
        link.href = country.path
        link.textContent = country.name
        const why = document.createElement("span")
        why.className = "ek-fine"
        why.textContent = country.reasons.join(" and ")
        const button = document.createElement("button")
        button.type = "button"
        button.className = "ek-btn ek-btn--small"
        button.textContent = "Follow"
        button.addEventListener("click", async () => {
            button.disabled = true
            try {
                const response = await fetch(suggestions.follows_path, { method: "POST", credentials: "same-origin", headers, body: JSON.stringify({ iso3: country.iso3 }) })
                if (!response.ok) throw new Error(String(response.status))
                button.textContent = "Following"
                button.classList.add("ek-btn--ghost")
            } catch (_error) {
                button.disabled = false
                button.textContent = "Try again"
            }
        })
        item.append(link, why, button)
        items.push(item)
    }
    if (suggestions.state) {
        const item = document.createElement("li")
        item.className = "ek-suggest-item"
        const link = document.createElement("a")
        link.href = suggestions.alerts_path
        link.textContent = `${suggestions.state}'s races`
        const why = document.createElement("span")
        why.className = "ek-fine"
        why.textContent = "your state"
        item.append(link, why)
        items.push(item)
    }
    if (!items.length) return
    list.replaceChildren(...items)
    host.querySelector("[data-ek-suggest-dismiss]").addEventListener("click", () => {
        host.hidden = true
        fetch(suggestions.dismiss_path, { method: "DELETE", credentials: "same-origin", headers }).catch(() => {})
    })
    host.hidden = false
}

// ---- source popovers --------------------------------------------------------

function placePopover(details) {
    const pop = details.querySelector(".ek-cite-pop")
    if (!pop) return
    const anchor = details.getBoundingClientRect()
    const width = Math.min(360, window.innerWidth - 24)
    pop.style.width = `${width}px`
    const left = Math.max(12, Math.min(anchor.left, window.innerWidth - width - 12))
    pop.style.left = `${left}px`
    const below = anchor.bottom + 6
    pop.style.top = `${below}px`
    // Not enough room under the icon: open above it.
    requestAnimationFrame(() => {
        const height = pop.getBoundingClientRect().height
        if (below + height > window.innerHeight - 8 && anchor.top - height - 6 > 8) pop.style.top = `${anchor.top - height - 6}px`
    })
}

function initCites() {
    const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)").matches
    document.addEventListener("toggle", (event) => {
        if (event.target.matches?.("details.ek-cite") && event.target.open) {
            for (const other of document.querySelectorAll("details.ek-cite[open]")) if (other !== event.target) other.open = false
            placePopover(event.target)
        }
    }, true)

    // A dismissal also cancels pending hover work, so its timer cannot reopen
    // the popover after Escape, an outside click, a scroll or a resize.
    let timer = null
    const closeAll = () => {
        clearTimeout(timer)
        timer = null
        document.querySelectorAll("details.ek-cite[open]").forEach((details) => {
            details.open = false
            delete details.dataset.pinned
        })
    }
    window.addEventListener("scroll", closeAll, { passive: true })
    window.addEventListener("resize", closeAll)
    document.addEventListener("keydown", (event) => event.key === "Escape" && closeAll())
    document.addEventListener("click", (event) => {
        if (!event.target.closest?.("details.ek-cite")) closeAll()
    })

    if (!finePointer) return
    document.addEventListener("pointerover", (event) => {
        const details = event.target.closest?.("details.ek-cite")
        if (!details) return
        clearTimeout(timer)
        if (!details.open) timer = setTimeout(() => (details.open = true), 120)
    })
    document.addEventListener("pointerout", (event) => {
        const details = event.target.closest?.("details.ek-cite")
        if (!details || details.contains(event.relatedTarget)) return
        clearTimeout(timer)
        // A popover opened by a click stays; one opened by hovering goes when the pointer leaves.
        if (details.dataset.pinned !== "1") timer = setTimeout(() => (details.open = false), 200)
    })
    document.addEventListener("click", (event) => {
        const summary = event.target.closest?.("details.ek-cite > summary")
        if (!summary) return
        const details = summary.parentElement
        if (details.open && details.dataset.pinned !== "1") {
            // Opened by hovering: a click means "keep it", not "close it".
            event.preventDefault()
            details.dataset.pinned = "1"
        } else {
            details.dataset.pinned = details.open ? "0" : "1"
        }
    })
}

// ---- countdowns -------------------------------------------------------------

// The server words a countdown from ITS calendar day, and the page may be cached for
// a minute or more. Re-word it from the visitor's own day.
function wordCountdown(days) {
    if (days === 0) return "today"
    if (days === 1) return "tomorrow"
    if (days === -1) return "yesterday"
    if (days >= 2 && days <= 59) return `in ${days} days`
    if (days >= 60 && days <= 729) return `in ${Math.round(days / 30.4)} months`
    if (days <= -2 && days >= -59) return `${-days} days ago`
    if (days <= -60 && days >= -729) return `${Math.round(-days / 30.4)} months ago`
    return days > 0 ? `in ${Math.round(days / 365.25)} years` : `${Math.round(-days / 365.25)} years ago`
}

function initCountdowns() {
    const now = new Date()
    const todayUtc = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
    for (const node of document.querySelectorAll("[data-ek-countdown]")) {
        const [year, month, day] = node.dataset.ekCountdown.split("-").map(Number)
        if (!year) continue
        const days = Math.round((Date.UTC(year, month - 1, day) - todayUtc) / 86400000)
        node.textContent = wordCountdown(days)
    }
}

// The big countdown on cards: a number and its unit ("3" "days", "Today"). The page is
// cached for everyone, so the visitor's own day decides, as for the small countdowns.
function initBigCountdowns() {
    const now = new Date()
    const todayUtc = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
    for (const node of document.querySelectorAll("[data-ek-bigcount]")) {
        const [year, month, day] = node.dataset.ekBigcount.split("-").map(Number)
        if (!year) continue
        const days = Math.round((Date.UTC(year, month - 1, day) - todayUtc) / 86400000)
        const num = node.querySelector(".ek-card-num")
        let unit = node.querySelector(".ek-card-unit")
        if (!num || days < 0) continue
        const [n, u] = days === 0 ? ["Today", ""] : days === 1 ? ["1", "day"] : days < 100 ? [String(days), "days"] : [String(Math.round(days / 30.4)), "months"]
        num.textContent = n
        if (!unit && u) {
            unit = document.createElement("span")
            unit.className = "ek-card-unit"
            num.after(unit)
        }
        if (unit) unit.textContent = u
    }
}

// ---- forms ------------------------------------------------------------------

function initFilters() {
    for (const form of document.querySelectorAll("form[data-ek-autosubmit]")) {
        form.addEventListener("change", () => form.requestSubmit())
    }
}

function initCountrySearch() {
    const input = document.querySelector("[data-ek-country-search]")
    if (!input) return
    const options = new Map([...document.querySelectorAll(`#${input.getAttribute("list")} option`)].map((o) => [o.value.toLowerCase(), o.dataset.path]))
    input.addEventListener("change", () => {
        const path = options.get(input.value.trim().toLowerCase())
        if (path) window.location.assign(path)
    })
}

// ---- country context --------------------------------------------------------

async function loadContext() {
    const host = document.querySelector("[data-ek-context]")
    if (!host) return
    try {
        const response = await fetch(host.dataset.ekContext, { headers: { Accept: "text/html" } })
        if (!response.ok) throw new Error(String(response.status))
        host.innerHTML = await response.text()
    } catch (_error) {
        host.textContent = "The World Bank figures could not be loaded."
        host.className = "ek-muted"
    }
}

// "7:00" in an alert or a digest is the reader's own 7:00, so tell the server the
// browser's zone once, when it has none (it never overwrites a zone chosen in Settings).
function reportTimeZone(me) {
    if (!me.signed_in || me.time_zone) return
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (!zone) return
    fetch(me.time_zone_path, {
        method: "PATCH",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": me.csrf_token },
        body: JSON.stringify({ time_zone: zone })
    }).catch(() => {})
}

function initSelectOnFocus() {
    for (const input of document.querySelectorAll("[data-ek-select]")) input.addEventListener("focus", () => input.select())
}

ready(() => {
    document.addEventListener("elekt:me", (event) => {
        initFollow(event.detail)
        initSuggestions(event.detail)
        reportTimeZone(event.detail)
    })
    initSelectOnFocus()
    loadMe()
    initCites()
    initCountdowns()
    initBigCountdowns()
    initFilters()
    initCountrySearch()
    initSubnav()
    loadContext()
})

// ---- subnav: where am I ----------------------------------------------------------

// The country page's sticky "On this page" nav marks the section being read: the last one whose top has passed
// 30% down the screen (at the very bottom of the page, the last one). On a phone the nav scrolls sideways to keep the
// marked link in view. Plain scroll + rAF: a handful of sections, measured once a frame. (A hidden tab throttles
// rAF, so a scripted test in one sees the mark a frame late.)
function initSubnav() {
    const nav = document.querySelector(".ek-subnav")
    if (!nav) return
    // Stick flush under the header at its real height (fonts, zoom and wrapping change it), not a guessed rem.
    const header = document.querySelector(".ek-header")
    if (header && "ResizeObserver" in window) {
        new ResizeObserver(() => {
            document.documentElement.style.setProperty("--ek-header-h", `${header.getBoundingClientRect().height}px`)
        }).observe(header)
    }
    const inner = nav.querySelector(".ek-subnav-inner") || nav
    const items = [...nav.querySelectorAll('a[href^="#"]')].flatMap((link) => {
        const target = document.getElementById(link.getAttribute("href").slice(1))
        return target ? [{ link, section: target.closest("section") || target }] : []
    })
    if (items.length === 0) return

    let current = null
    const mark = () => {
        // A section is "being read" once its top passes 30% down the screen (and always once it is under the nav).
        const line = Math.max(nav.getBoundingClientRect().bottom + 16, window.innerHeight * 0.3)
        const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4
        let active = null
        for (const item of items) if (item.section.getBoundingClientRect().top <= line) active = item
        if (atBottom && items.at(-1).section.getBoundingClientRect().top < window.innerHeight) active = items.at(-1)
        if (active === current) return
        current?.link.removeAttribute("aria-current")
        current = active
        if (!active) return
        active.link.setAttribute("aria-current", "location")
        const { offsetLeft, offsetWidth } = active.link
        if (offsetLeft < inner.scrollLeft || offsetLeft + offsetWidth > inner.scrollLeft + inner.clientWidth) {
            inner.scrollTo({ left: offsetLeft - 16, behavior: "smooth" })
        }
    }
    let queued = false
    const onScroll = () => {
        if (queued) return
        queued = true
        requestAnimationFrame(() => { queued = false; mark() })
    }
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll, { passive: true })
    mark()
}

// A news image the outlet will not serve here (hotlink protection, a removed photo) disappears with its frame, rather
// than leaving a broken-image icon: the card reads fine without it.
document.addEventListener("error", (event) => {
    const img = event.target
    if (img instanceof HTMLImageElement && img.closest(".ek-news-image")) img.closest(".ek-news-image").remove()
}, true)
